import classNames from 'classnames';
import { useFormikContext } from 'formik-2';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import { IconVariants } from '../../../components/foundations/Icon';
import Table from '../../../components/organisms/Table';
import { Button } from '../../../components/widgets';
import Select from '../../../components/widgets/Select';
import { getPostbackMacroNetworkName } from '../../../selectors/partnerIntegration';
import {
    ATTRIBUTION_TAB_IDS,
    REQUIRED_SERVER_SIDE_MACRO_KEYS,
    STANDARD_TRACKING_LINK_MAP,
    STANDARD_TRACKING_LINK_PARAMETERS,
    TRACKING_LINK_CATEGORIES,
    TRACKING_LINK_EXTRA_REQUIRED_KEYS,
    TRACKING_LINK_EXTRA_RULE_PLATFORMS,
    TRANSLATE_PREFIX,
    URL_QUERY_KEY_REGEX,
    URL_QUERY_VALUE_REGEX,
    buildPassthroughAdnetworkMacro,
    generalPlatformsIncludeTrackingLinkExtraRulePlatform,
    isRecommendedMacroKey,
} from '../../consts';
import { PLATFORM_TEMPLATE_DEFAULTS } from '../../platformTemplateDefaults';
import useConfirmDialog from '../../useConfirmDialog';
import useStableRowKeys from '../../useStableRowKeys';
import DropdownOptionLabel from '../DropdownOptionLabel';
import sharedTableCss from '../formTable.css';
import UrlPreview from '../UrlPreview';
import BulkRemoveUsedParamsBody from './BulkRemoveUsedParamsBody';
import { FIELD_PATH, FILTER_BUTTONS } from './consts';
import { getEmptyClearableIndices } from './getEmptyClearableIndices';
import css from './TrackingLinkParametersTable.css';
import { getTableMetadata, isServerSideMethod } from './utils';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.TRACKING_LINK_OPTIONS_TAB`;
const PLATFORM_LABEL_PREFIX = `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORM_OPTIONS`;
const TRACKING_LINK_PREVIEW_BASE = 'https://sng.link/A1b2c/example';
const GENERAL_TAB_ID = ATTRIBUTION_TAB_IDS.GENERAL;
const TEMPLATES_TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES;
const paramT = key => `${TAB_PREFIX}.PARAMS.${key.toUpperCase()}`;

const getCategory = param => {
    const def = STANDARD_TRACKING_LINK_MAP[param.singular_key];
    return def ? def.category : TRACKING_LINK_CATEGORIES.CUSTOM;
};

const TrackingLinkParametersTable = ({ translate, readOnly = false }) => {
    const postbackMacroNetworkName = useSelector(getPostbackMacroNetworkName);
    const { values, setFieldValue } = useFormikContext();
    const tabData = values.formData?.[ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS] || {};
    const generalPlatforms = values.formData?.[GENERAL_TAB_ID]?.platforms;
    const generalPlatformsNeedTrackingLinkExtraRule = generalPlatformsIncludeTrackingLinkExtraRulePlatform(
        generalPlatforms
    );
    const extraRuleMacroErrorTranslateData = useMemo(() => {
        if (!generalPlatformsNeedTrackingLinkExtraRule) return null;
        const triggering = (generalPlatforms || []).filter(p => TRACKING_LINK_EXTRA_RULE_PLATFORMS.includes(p));
        const platformsList = triggering
            .map(p => translate(`${PLATFORM_LABEL_PREFIX}.${String(p).toUpperCase()}`))
            .join(', ');
        return { platformsList };
    }, [generalPlatforms, generalPlatformsNeedTrackingLinkExtraRule, translate]);
    const parameters = useMemo(() => tabData.tracking_link_parameters || [], [tabData.tracking_link_parameters]);
    const isServerSide = isServerSideMethod(tabData.click_impression_reporting_method);
    const platformTemplate = values.formData?.[GENERAL_TAB_ID]?.platform_template;
    const templatePartnerMacros = PLATFORM_TEMPLATE_DEFAULTS[platformTemplate]?.tracking_link_options?.partner_macros;

    const [filter, setFilter] = useState(TRACKING_LINK_CATEGORIES.ALL);

    const { keysRef: rowKeysRef, makeKey: makeRowKey } = useStableRowKeys(parameters.length, 'param');

    const { confirm, dialog } = useConfirmDialog();
    const templates = values.formData?.[TEMPLATES_TAB_ID];

    const templatesUseMacroKey = key => {
        if (!key) return false;
        const macro = buildPassthroughAdnetworkMacro(key, postbackMacroNetworkName);
        return Object.values(templates || {}).some(byPlatform =>
            Object.values(byPlatform || {}).some(rows => (rows || []).some(r => r.singular_macro === macro))
        );
    };

    // True if any tracking-link param other than `excludeIndex` still owns this key — i.e. the
    // template's passthrough `{key?ApsalarName}` reference would remain valid even after this row is removed/renamed.
    const keyHasOtherProvider = (key, excludeIndex) =>
        !!key && parameters.some((p, i) => i !== excludeIndex && (p.singular_key || '').trim() === key);

    const stripMacroKeysFromTemplates = keys => {
        if (!keys.length || !templates) return;
        const macros = new Set(
            keys.filter(Boolean).map(k => buildPassthroughAdnetworkMacro(k, postbackMacroNetworkName))
        );
        if (!macros.size) return;
        const next = {};
        Object.entries(templates).forEach(([eventType, byPlatform]) => {
            const newPlatforms = {};
            Object.entries(byPlatform || {}).forEach(([platform, rows]) => {
                const filtered = (rows || []).filter(r => !macros.has(r.singular_macro));
                newPlatforms[platform] = filtered;
            });
            next[eventType] = newPlatforms;
        });
        setFieldValue(`formData.${TEMPLATES_TAB_ID}`, next);
    };
    const stripMacroKeyFromTemplates = key => stripMacroKeysFromTemplates([key]);

    const setParams = updated => setFieldValue(FIELD_PATH, updated);
    const applyParam = (index, field, value) => {
        const normalizedValue = field === 'partner_macro' && value === '' ? undefined : value;
        setParams(parameters.map((p, i) => (i === index ? { ...p, [field]: normalizedValue } : p)));
    };

    const setParam = (index, field, value) => {
        const param = parameters[index];
        const isRenamingCustomKey = field === 'singular_key' && param?.is_custom;
        const oldKey = isRenamingCustomKey ? (param.singular_key || '').trim() : '';
        const newKey = isRenamingCustomKey ? (typeof value === 'string' ? value.trim() : '') : '';
        const oldKeyInUseByTemplates =
            !!oldKey && oldKey !== newKey && templatesUseMacroKey(oldKey) && !keyHasOtherProvider(oldKey, index);

        // Apply the change first so the input always reflects what the user typed.
        applyParam(index, field, value);

        // If templates still reference the old key (and no other tracking-link row still owns it),
        // confirm; on cancel revert formik to the old key (TextField's useEffect will then re-sync).
        if (oldKeyInUseByTemplates) {
            confirm({
                titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.RENAME_USED_PARAM_TITLE`,
                textKey: `${TAB_PREFIX}.CONFIRM_DIALOG.RENAME_USED_PARAM_TEXT`,
                onConfirm: () => stripMacroKeyFromTemplates(oldKey),
                onCancel: () => setFieldValue(`${FIELD_PATH}[${index}].singular_key`, oldKey),
            });
        }
    };

    const applyRemove = index => {
        rowKeysRef.current = rowKeysRef.current.filter((_, i) => i !== index);
        setParams(parameters.filter((_, i) => i !== index));
    };

    const addedKeys = new Set(parameters.map(p => p.singular_key));
    const availableSecondary = STANDARD_TRACKING_LINK_PARAMETERS.filter(m => !addedKeys.has(m.singularKey));

    const addStandardMacro = singularKey => {
        const def = STANDARD_TRACKING_LINK_MAP[singularKey];
        if (!def) return;
        rowKeysRef.current = [...rowKeysRef.current, makeRowKey()];
        setParams([
            ...parameters,
            {
                singular_key: singularKey,
                field_name: translate(`${paramT(singularKey)}.FIELD_NAME`),
                description: translate(`${paramT(singularKey)}.DESCRIPTION`),
                partner_macro: undefined,
            },
        ]);
    };

    const addCustomMacro = () => {
        rowKeysRef.current = [...rowKeysRef.current, makeRowKey()];
        setParams([
            ...parameters,
            { singular_key: '', field_name: '', description: '', partner_macro: undefined, is_custom: true },
        ]);
    };

    const isCustomRow = p => !!p?.is_custom;

    const getCategoryForParam = p => (isCustomRow(p) ? TRACKING_LINK_CATEGORIES.CUSTOM : getCategory(p));

    const activeCategories = useMemo(
        () => new Set(parameters.map(p => (isCustomRow(p) ? TRACKING_LINK_CATEGORIES.CUSTOM : getCategory(p)))),
        [parameters]
    );

    useEffect(() => {
        if (filter !== TRACKING_LINK_CATEGORIES.ALL && !activeCategories.has(filter)) {
            setFilter(TRACKING_LINK_CATEGORIES.ALL);
        }
    }, [activeCategories, filter]);

    const filteredRows = parameters
        .map((p, i) => ({ p, i }))
        .filter(({ p }) => filter === TRACKING_LINK_CATEGORIES.ALL || getCategoryForParam(p) === filter);

    const secondaryOptions = availableSecondary.map(m => ({
        value: m.singularKey,
        label: translate(`${paramT(m.singularKey)}.FIELD_NAME`),
    }));

    const filterSecondaryOption = ({ value, label }, input) => {
        const q = input.toLowerCase();
        return value.toLowerCase().includes(q) || label.toLowerCase().includes(q);
    };

    const singularKeyCounts = {};
    parameters.forEach(p => {
        if (p.singular_key) singularKeyCounts[p.singular_key] = (singularKeyCounts[p.singular_key] || 0) + 1;
    });

    // Singular keys that are currently required by the active reporting method or general-tab
    // platform selection cannot be removed — deleting them would leave the form invalid in a way
    // the user can't reach via the "Add" dropdown without remembering the exact key.
    const mandatoryKeys = new Set([
        ...(isServerSide ? REQUIRED_SERVER_SIDE_MACRO_KEYS : []),
        ...(generalPlatformsNeedTrackingLinkExtraRule ? TRACKING_LINK_EXTRA_REQUIRED_KEYS : []),
    ]);

    const getMacroErrorKey = p => {
        if (p.partner_macro && !URL_QUERY_VALUE_REGEX.test(p.partner_macro))
            return 'TRACKING_LINK_PARAM_MACRO_INVALID_CHARS';
        if (isServerSide && REQUIRED_SERVER_SIDE_MACRO_KEYS.includes(p.singular_key) && !p.partner_macro)
            return 'TRACKING_LINK_PARAM_MACRO_REQUIRED';
        if (
            generalPlatformsNeedTrackingLinkExtraRule &&
            TRACKING_LINK_EXTRA_REQUIRED_KEYS.includes(p.singular_key) &&
            !p.partner_macro
        )
            return 'TRACKING_LINK_PARAM_EXTRA_RULE';
        return null;
    };

    const isBracketed = macro => /\{[^}]+\}/.test(macro) || /^%.*%$/.test(macro);

    const getMacroWarningKey = (p, macroErrorKey, isRecommended = false) => {
        if (p.partner_macro && !macroErrorKey && !isBracketed(p.partner_macro))
            return 'TRACKING_LINK_PARAM_MACRO_BRACKET_WARNING';
        if (!p.partner_macro && isRecommended) return 'TRACKING_LINK_PARAM_MACRO_RECOMMENDED';
        return null;
    };

    // Three-tier ordering: required first (locked-down rows the user can't delete), then
    // recommended (carry the "Highly recommended" hint and benefit from being visible), then the
    // rest. Stable sort (ES2019+) preserves the relative order within each tier.
    const rankRow = p => {
        if (mandatoryKeys.has(p.singular_key)) return 0;
        if (
            isRecommendedMacroKey(p.singular_key, {
                method: tabData.click_impression_reporting_method,
                generalPlatforms,
            })
        )
            return 1;
        return 2;
    };
    const sortedRows = [...filteredRows].sort((a, b) => rankRow(a.p) - rankRow(b.p));

    // Scoped to filteredRows: when a category filter is active, hidden-category empties
    // (including template-blocked ones) are intentionally ignored — the button lives in
    // the filter row so its action applies to what the user currently sees.
    const {
        safe: safeEmptyIndices,
        blockedByTemplates: templateBlockedEmpties,
    } = getEmptyClearableIndices(filteredRows, { mandatoryKeys, templatesUseMacroKey, keyHasOtherProvider });
    const hasAnyClearable = safeEmptyIndices.length > 0 || templateBlockedEmpties.length > 0;
    const removeAtIndices = indices => {
        if (!indices.length) return;
        const toRemove = new Set(indices);
        rowKeysRef.current = rowKeysRef.current.filter((_, i) => !toRemove.has(i));
        setParams(parameters.filter((_, i) => !toRemove.has(i)));
    };
    const clearEmptyRows = () => {
        if (!hasAnyClearable) return;
        if (!templateBlockedEmpties.length) {
            removeAtIndices(safeEmptyIndices);
            return;
        }
        const blockedIndices = templateBlockedEmpties.map(b => b.index);
        const blockedKeys = templateBlockedEmpties.map(b => b.key);
        confirm({
            titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.REMOVE_USED_PARAMS_BULK_TITLE`,
            body: <BulkRemoveUsedParamsBody keys={blockedKeys} />,
            onConfirm: () => {
                removeAtIndices([...safeEmptyIndices, ...blockedIndices]);
                stripMacroKeysFromTemplates(blockedKeys);
            },
            onCancel: () => removeAtIndices(safeEmptyIndices),
        });
    };
    const tableData = sortedRows.map(({ p, i }) => {
        const isCustom = isCustomRow(p);
        const def = isCustom ? undefined : STANDARD_TRACKING_LINK_MAP[p.singular_key];
        const isPrimary = def?.primary ?? false;
        const category = isCustom ? TRACKING_LINK_CATEGORIES.CUSTOM : getCategory(p);
        const isRecommended = isRecommendedMacroKey(p.singular_key, {
            method: tabData.click_impression_reporting_method,
            generalPlatforms,
        });
        const macroErrorKey = getMacroErrorKey(p);
        const macroWarningKey = getMacroWarningKey(p, macroErrorKey, isRecommended);
        const macroErrorTranslateData =
            macroErrorKey === 'TRACKING_LINK_PARAM_EXTRA_RULE' ? extraRuleMacroErrorTranslateData : undefined;
        const oldKey = (p.singular_key || '').trim();
        // Any row (custom or standard) that's referenced by a postback template's passthrough
        // macro `{oldKey?network}` triggers the confirm flow on delete — the macro is no longer
        // a valid enum option once this is the only row owning it.
        const removeUsedByTemplates = !!oldKey && templatesUseMacroKey(oldKey) && !keyHasOtherProvider(oldKey, i);
        const paramHasUserContent =
            !!(p.partner_macro || '').trim() ||
            (!!isCustom && !!(oldKey || (p.field_name || '').trim() || (p.description || '').trim()));
        const templateMacro = templatePartnerMacros?.[p.singular_key];
        const isAutoFilled = !!p.partner_macro && !!templateMacro && p.partner_macro === templateMacro;
        return {
            key: rowKeysRef.current[i],
            index: i,
            singular_key: p.singular_key,
            field_name: p.field_name,
            description: p.description,
            partner_macro: p.partner_macro,
            category,
            isPrimary,
            isCustomRow: isCustom,
            isMandatory: mandatoryKeys.has(p.singular_key),
            isRequired: !readOnly,
            isRecommended,
            isAutoFilled,
            macroErrorKey,
            macroErrorTranslateData,
            macroWarningKey,
            duplicateKey: !readOnly && !!p.singular_key && singularKeyCounts[p.singular_key] > 1,
            singularKeyInvalidChars:
                !readOnly && isCustom && !!p.singular_key && !URL_QUERY_KEY_REGEX.test(p.singular_key),
            deleteConfirmTitleKey: removeUsedByTemplates
                ? `${TAB_PREFIX}.CONFIRM_DIALOG.REMOVE_USED_PARAM_TITLE`
                : `${TAB_PREFIX}.CONFIRM_DIALOG.REMOVE_PARAM_TITLE`,
            deleteConfirmTextKey: removeUsedByTemplates
                ? `${TAB_PREFIX}.CONFIRM_DIALOG.REMOVE_USED_PARAM_TEXT`
                : `${TAB_PREFIX}.CONFIRM_DIALOG.REMOVE_PARAM_TEXT`,
            onConfirmDelete: () => {
                if (removeUsedByTemplates) stripMacroKeyFromTemplates(oldKey);
                applyRemove(i);
            },
            deleteRequiresConfirm: removeUsedByTemplates || paramHasUserContent,
        };
    });

    const sharedCellProps = { setParam, readOnly, translate };
    const tableMetadata = getTableMetadata({ sharedCellProps, readOnly });

    return (
        <div className={css.container}>
            <div className={css.sectionHeader}>
                <div className={css.title}>
                    <Translate id={`${TAB_PREFIX}.PARAMS_TITLE`} />
                </div>
                <p className={css.subtitle}>
                    <Translate id={`${TAB_PREFIX}.PARAMS_SUBTITLE`} />
                </p>
            </div>
            <div className={css.preview}>
                <div className={css.previewTitle}>
                    <Translate id={`${TAB_PREFIX}.PREVIEW_TITLE`} />
                </div>
                <p className={css.previewSubtitle}>
                    <Translate id={`${TAB_PREFIX}.PREVIEW_SUBTITLE`} />
                </p>
                <UrlPreview
                    baseUrl={TRACKING_LINK_PREVIEW_BASE}
                    pairs={parameters.map(p => ({ key: p.singular_key, value: p.partner_macro }))}
                />
            </div>
            <div className={css.filterRow}>
                <span className={css.filterLabel}>
                    <Translate id={`${TAB_PREFIX}.FILTER_BY`} />
                </span>
                {FILTER_BUTTONS.map(({ key, labelKey }) => {
                    const isEmpty = key !== TRACKING_LINK_CATEGORIES.ALL && !activeCategories.has(key);
                    return (
                        <Button
                            key={key}
                            type="flat"
                            className={classNames(css.filterBtn, {
                                [css.filterBtnActive]: filter === key,
                                [css.filterBtnDisabled]: isEmpty,
                            })}
                            onClick={() => !isEmpty && setFilter(key)}
                            disabled={isEmpty}
                        >
                            <Translate id={labelKey} />
                        </Button>
                    );
                })}
            </div>
            <div className={css.tableWrapper}>
                <Table
                    metadata={tableMetadata}
                    data={tableData}
                    zebraTable={false}
                    rowHoverEnabled={false}
                    showInnerBorder={false}
                    tableClass={sharedTableCss.table}
                    theadClass={sharedTableCss.thead}
                    thClass={sharedTableCss.th}
                    trClass={sharedTableCss.row}
                    emptyState={
                        <span className={css.emptyState}>
                            <Translate id={`${TAB_PREFIX}.EMPTY_TABLE_STATE`} />
                        </span>
                    }
                />
            </div>
            {!readOnly && (
                <div className={css.addRow}>
                    <Select
                        options={secondaryOptions}
                        value={null}
                        onChange={opt => opt && addStandardMacro(opt.value)}
                        placeholder={translate(`${TAB_PREFIX}.ADD_STANDARD_MACRO`)}
                        isSearchable
                        isClearable={false}
                        isDisabled={!secondaryOptions.length}
                        className={css.addStandardSelect}
                        filterOption={filterSecondaryOption}
                        formatOptionLabel={DropdownOptionLabel}
                    />
                    <Button
                        type="link"
                        onClick={addCustomMacro}
                        Icon="plus"
                        iconVariant={IconVariants.ACTIVE}
                        className={css.addCustomMacroButton}
                    >
                        <Translate id={`${TAB_PREFIX}.ADD_CUSTOM_MACRO`} />
                    </Button>
                    <Button
                        disabled={!hasAnyClearable}
                        type="secondary"
                        onClick={clearEmptyRows}
                        Icon="trash"
                        iconVariant={IconVariants.ERROR}
                        className={css.deleteEmptyRowsButton}
                    >
                        <Translate id={`${TAB_PREFIX}.DELETE_EMPTY_ROWS_LABEL`} />
                    </Button>
                </div>
            )}
            {dialog}
        </div>
    );
};

TrackingLinkParametersTable.propTypes = {
    translate: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
};

export default withLocalize(TrackingLinkParametersTable);
